import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  getIsCheckedDistrictCourtCasesAddedManually,
  getIsCheckedDistrictCourtCasesByCompany,
  getIsCheckedHighCourtCasesAddedManually,
  getIsCheckedHighCourtCasesByCompany,
  getIsCheckedOtherCourtCasesAddedManually,
  AssignCasesInBulk,
  getCompanyLimitedStats,
} from "../../services/api/CasesApi.js";
import { useSearchResults } from "../../context/SearchResultsContext";
import "./CaseList.scss";
import ActionBar from "../../components/ActionBar/ActionBar";
import CaseCard from "../../components/CaseCard/CaseCard";
import { getAddedCases } from "../../services/api/AddedApi.js";
import { useLoading } from "../../context/LoadingContext.js";
import {
  getDistrictCourtCustomerCases,
  getHighCourtCustomerCases,
  getOtherCourtCustomerCases,
} from "../../services/api/CustomerDashApi.js";
import { useAuth } from "../../context/AuthContext.js";
import { useCaseSelection } from "../../context/CaseSelectionContext.js";
import CompanyLimitModal from "../../components/CompanyModalLimit/CompanyModalLimit.js";
import { getHierarchy } from "../../services/api/HierarchyApi.js";
import { useToast } from "../../context/ToastContext.js";
import { useBubbleSettings } from "../../context/BubbleSettingsContext.js";
import JusticeLoader from "../../components/JusticeLoader/JusticeLoader.js";
import { formatText } from "../../components/TruncateParties.js";
import { getLabelsByUser } from "../../services/api/LabelsApi.js";
import { getCompanyLimitedStatsById } from "../../services/api/CasesApi.js";

export default function CaseList({ username }) {
  const { showToast } = useToast();
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState([]);

  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [showLawyerDropdown, setShowLawyerDropdown] = useState(false);
  const [lawyers, setLawyers] = useState([]);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [lawyerSearchTerm, setLawyerSearchTerm] = useState("");
  const [focusedLawyerIndex, setFocusedLawyerIndex] = useState(0);
  const { fetchAndUpdateBubbleSettings } = useBubbleSettings();

  // Updated context destructuring with new naming
  const {
    selectedCaseType,
    selectedCourtType,
    filteredCases, // This contains the filtered results from DetailSection
    filters,
    setFilters,
    selectedAssignedUser,
    setSelectedAssignedUser,
    selectedLabel: contextSelectedLabel,
    setSelectedLabel: setContextSelectedLabel,
    hasActiveFilters, // This tells us if any filters are active
    clearAllFilters,
    searchTerm, // The current search term
    selectedCaseCard, // The selected card filter
    handleCaseCardClick, // Handler for card clicks
    isLoading, // Add this
    setIsLoading,
    pagination, 
    setPagination,
    triggerCaseRefresh, // Add this to trigger refresh after modal update
  } = useCaseSelection();

  const {
    searchResults,
    setSearchResults,
    searchType,
    setSearchType,
    searchMetadata,
    setSearchMetadata,
  } = useSearchResults();

  const [selected, setSelected] = useState({});
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const [hasPerformedSearch, setHasPerformedSearch] = useState(false);

  const [isAssigning, setIsAssigning] = useState(false);

  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const [labels, setLabels] = useState([]);
  const [labelSearchTerm, setLabelSearchTerm] = useState("");
  const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);

  const { role, company } = useAuth();
  const [prefetchedStats, setPrefetchedStats] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [persistedSelectedRefs, setPersistedSelectedRefs] = useState(new Set());

  const prevTabRef = useRef(`${selectedCaseType}_${selectedCourtType}`);
  const fetchingRef = useRef(false);
  const renderCountRef = useRef(0);
  const activeSearchTermRef = useRef("");

  renderCountRef.current += 1;
  const fetchCompanyStats = useCallback(async () => {
    if (!company?._id) return;
    try {
      const response = await getCompanyLimitedStatsById(company._id);
      setPrefetchedStats(response.data);
    } catch (error) {
      console.error("Error pre-fetching company stats:", error);
    }
  }, [company]);

  useEffect(() => {
    fetchCompanyStats();
  }, [fetchCompanyStats]);

  const handleManageCases = useCallback(() => {
    setIsCompanyModalOpen(true);
  }, []);

  const handleModalUpdateSuccess = useCallback(() => {
    triggerCaseRefresh();
    fetchCompanyStats(); // <--- Refresh stats here too
  }, [triggerCaseRefresh, fetchCompanyStats]);

  const filteredLawyers = useMemo(() => {
    return lawyers.filter(
      (lawyer) =>
        lawyer.name.toLowerCase().includes(lawyerSearchTerm.toLowerCase()) ||
        lawyer.email.toLowerCase().includes(lawyerSearchTerm.toLowerCase())
    );
  }, [lawyers, lawyerSearchTerm]);

  const handleLawyerSelect = useCallback((lawyer) => {
    setSelectedLawyers((prev) => {
      const isSelected = prev.some((l) => l._id === lawyer._id);
      if (isSelected) {
        return prev.filter((l) => l._id !== lawyer._id);
      } else {
        return [...prev, lawyer];
      }
    });
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getHierarchy();
        setUsers(response?.data?.users || response?.users || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await getLabelsByUser("?limit=1000");
        setLabels(response?.data?.labels || []);
      } catch (error) {
        console.error("Error fetching labels:", error);
        setLabels([]);
      }
    };

    if (role !== "customer") {
      fetchLabels();
    }
  }, [role]);

  useEffect(() => {
    const fetchLawyers = async () => {
      try {
        const response = await getHierarchy();
        setLawyers(response.data?.users || []);
      } catch (error) {
        console.error("Error fetching lawyers:", error);
        setLawyers([]);
      }
    };

    if (showCheckboxes) {
      fetchLawyers();
    }
  }, [showCheckboxes]);

  useEffect(() => {
    const currentTab = `${selectedCaseType}_${selectedCourtType}`;
    if (prevTabRef.current !== currentTab) {

      fetchingRef.current = false;
      activeSearchTermRef.current = "";

      setSelected({});
      // setCurrentPage(0); // No longer needed, context handles this

      // CRITICAL: Use context's clearAllFilters for consistent clearing
      clearAllFilters();

      prevTabRef.current = currentTab;
    }
  }, [selectedCaseType, selectedCourtType, clearAllFilters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleUserSelectMultiple = useCallback((userId) => {
    setSelectedAssignedUsers((prev) => {
      if (userId === "") {
        return []; // Clear all selections
      }

      const isSelected = prev.includes(userId);
      if (isSelected) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
    setIsUserDropdownOpen(false);
    setUserSearchTerm("");
  }, []);

  // REMOVED: Redundant useEffect, context setters handle resetting pagination
  // useEffect(() => {
  //   setCurrentPage(0);
  // }, [filters]);

  const filteredUsers = useMemo(() => {
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
  }, [users, userSearchTerm]);

  const selectedUserName = useMemo(() => {
    const user = users.find((u) => u._id === selectedAssignedUser);
    return user ? user.name : "";
  }, [users, selectedAssignedUser]);

  const handleUserSelect = useCallback(
    (userId) => {
      setIsLoading(true); // Start loading
      setSelectedAssignedUser(userId);
      setIsUserDropdownOpen(false);
      setUserSearchTerm("");
    },
    [setSelectedAssignedUser, setIsLoading]
  );

  const clearUserFilter = useCallback(() => {
    setSelectedAssignedUser("");
    setUserSearchTerm("");
    setIsUserDropdownOpen(false);
  }, [setSelectedAssignedUser]);

  const filteredLabels = useMemo(() => {
    return labels.filter((labelGroup) =>
      labelGroup.name.toLowerCase().includes(labelSearchTerm.toLowerCase())
    );
  }, [labels, labelSearchTerm]);

  const selectedLabelName = useMemo(() => {
    for (const labelGroup of labels) {
      if (labelGroup.labels && labelGroup.labels[0]?._id === contextSelectedLabel) {
        return labelGroup.name;
      }
    }
    return "";
  }, [labels, contextSelectedLabel]);

  const handleLabelSelect = useCallback(
    (labelId) => {
      setIsLoading(true);
      setContextSelectedLabel(labelId);
      setIsLabelDropdownOpen(false);
      setLabelSearchTerm("");
    },
    [setIsLoading, setContextSelectedLabel]
  );

  const clearLabelFilter = useCallback(() => {
    setContextSelectedLabel("");
    setLabelSearchTerm("");
    setIsLabelDropdownOpen(false);
  }, [setContextSelectedLabel]);

  const displayCases = useMemo(() => {

    // Priority 1: If we have search results, use them
    if (searchResults.length > 0 && searchType === "cases") {
      return searchResults;
    }

    // Priority 2: Use filtered cases from context (this includes filter results)
    return filteredCases;
  }, [filteredCases, searchResults, searchType, hasActiveFilters]);

  // REMOVED: Frontend pagination logic
  // const paginatedCases = useMemo(() => {
  //   const startIndex = currentPage * casesPerPage;
  //   const endIndex = startIndex + casesPerPage;
  //   return displayCases.slice(startIndex, endIndex);
  // }, [displayCases, currentPage, casesPerPage]);

  // `displayCases` now holds the already paginated data from the backend
  const paginatedCases = displayCases;

  const handleAssignCases = useCallback(async () => {
    const selectedCaseIndices = Object.keys(selected).filter(
      (key) => selected[key]
    );
    if (selectedCaseIndices.length === 0) {
      showToast("Please select at least one case to assign", "error");
      return;
    }

    if (selectedLawyers.length === 0) {
      showToast(
        "Please select at least one lawyer to assign cases to",
        "error"
      );
      return;
    }

    setIsAssigning(true);

    try {
      const courtRefs = selectedCaseIndices
        .map((index) => {
          // Use paginatedCases since indices match paginated cases
          const caseData = paginatedCases[parseInt(index)];
          return caseData?.viewDetails?.courtRef;
        })
        .filter(Boolean);

      const userIds = selectedLawyers.map((lawyer) => lawyer._id);

      const payload = {
        courtRef: courtRefs,
        users: userIds,
      };

      await AssignCasesInBulk(payload);

      showToast(
        `Successfully assigned ${courtRefs.length} cases to ${selectedLawyers.length} lawyer(s)`,
        "success"
      );

      setShowCheckboxes(false);
      setShowLawyerDropdown(false);
      setSelected({});
      setSelectedLawyers([]);
      setLawyerSearchTerm("");

      // Refetch cases to update assignment status - this will now trigger DetailSection to refetch
      // ADDED: Fetch and update bubble settings after successful assignment
      await fetchAndUpdateBubbleSettings();
      triggerCaseRefresh()
    } catch (error) {
      console.error("Error assigning cases:", error);
      showToast("Failed to assign cases. Please try again.", "error");
    } finally {
      setIsAssigning(false);
    }
  }, [
    selected,
    selectedLawyers,
    paginatedCases, // This is now the pre-paginated list
    showToast,
    fetchAndUpdateBubbleSettings,
    triggerCaseRefresh
  ]);

  const allSelected = useMemo(() => {
    const selectedValues = Object.values(selected);
    return selectedValues.length > 0 && selectedValues.every((v) => v);
  }, [selected]);

  const toggleAll = useCallback(() => {
    const newSelected = Object.fromEntries(
      paginatedCases.map((_, index) => [index, !allSelected])
    );
    setSelected(newSelected);
  }, [paginatedCases, allSelected]);

  const toggleOne = useCallback((index) => {
    setSelected((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  // Reset focused index when dropdown opens or filtered lawyers change
  useEffect(() => {
    if (showLawyerDropdown && filteredLawyers.length > 0) {
      setFocusedLawyerIndex(0);
    }
  }, [showLawyerDropdown, filteredLawyers.length]);

  // Keyboard navigation for lawyer dropdown
  useEffect(() => {
    if (!showLawyerDropdown) return;

    const handleKeyDown = (e) => {
      // Only handle keyboard events if the dropdown is visible
      if (!showLawyerDropdown) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedLawyerIndex((prev) =>
            prev < filteredLawyers.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedLawyerIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case " ":
          e.preventDefault();
          if (filteredLawyers[focusedLawyerIndex]) {
            handleLawyerSelect(filteredLawyers[focusedLawyerIndex]);
          }
          break;
        case "Enter":
          e.preventDefault();
          handleAssignCases();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showLawyerDropdown, filteredLawyers, focusedLawyerIndex, handleLawyerSelect, handleAssignCases]);

  const shouldShowNoCasesMessage = useMemo(() => {
    // Check total cases from pagination info
    return !isLoading && pagination.totalCases === 0;
  }, [isLoading, pagination.totalCases]);


  const getNoCasesMessage = useMemo(() => {
    // Check if any filters are active using context's hasActiveFilters
    if (hasActiveFilters) {
      return "No cases found matching the applied filters.";
    }

    // Check if we're in search mode but have no results
    if (searchType === "cases") {
      return "No cases found matching your search.";
    }

    // Default messages based on court type
    if (selectedCourtType === "OtherCourt") {
      return "No Other Court cases found.";
    } else if (selectedCourtType === "HighCourt") {
      return `No ${selectedCaseType === "fetched" ? "fetched" : "added"
        } High Court cases found.`;
    } else if (selectedCourtType === "DistrictCourt") {
      return `No ${selectedCaseType === "fetched" ? "fetched" : "added"
        } District Court cases found.`;
    } else if (selectedCourtType === "SupremeCourt") {
      return `No ${selectedCaseType === "fetched" ? "fetched" : "added"
        } Supreme Court cases found.`;
    }

    return "No cases found.";
  }, [
    hasActiveFilters,
    displayCases.length,
    searchType,
    selectedCourtType,
    selectedCaseType,
  ]);

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  // Lawyer dropdown refs and position state
  const lawyerButtonRef = useRef();
  const lawyerDropdownRef = useRef();
  const [lawyerDropdownPosition, setLawyerDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      const dropdown = document.querySelector(
        ".filter-dropdown-box.main-case-filter-dropdown-box"
      );
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  // Lawyer dropdown positioning logic (similar to filter dropdown)
  useEffect(() => {
    if (showLawyerDropdown && lawyerButtonRef.current) {
      const buttonRect = lawyerButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      
      // If dropdown would go off right edge, align to right of button
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      
      // If still off left edge, push it in
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      
      setLawyerDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 500,
      });
    }

    const handleScroll = (e) => {
      if (!showLawyerDropdown) return;
      const dropdown = lawyerDropdownRef.current;
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        return;
      }
      setShowLawyerDropdown(false);
    };

    if (showLawyerDropdown) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showLawyerDropdown]);

  const handleCaseDeleted = useCallback((deletedCourtRef) => {
    // Case deletion will trigger a refetch in DetailSection
    console.log("Case deleted and list will refresh");
  }, []);

  const handleFilterChange = useCallback(
    (field, value) => {
      setIsLoading(true); // Start loading
      setFilters((prev) => ({ ...prev, [field]: value }));
    },
    [setFilters, setIsLoading]
  );

  const clearFilters = useCallback(() => {
    // Use context's clearAllFilters to reset everything consistently
    clearAllFilters();

    // Also reset local UI state
    // setCurrentPage(0); // No longer needed
    setSelected({});

    // Clear the search input field
    const searchInputs = document.querySelectorAll(
      ".detailsection-search-input, input[placeholder*='Search']"
    );
    searchInputs.forEach((input) => {
      if (input.value !== "") {
        input.value = "";
        const event = new Event("input", { bubbles: true });
        input.dispatchEvent(event);
      }
    });
  }, [clearAllFilters]);

  // NEW: Handler for changing cases per page
  const handleCasesPerPageChange = useCallback((e) => {
    const value = e.target.value;
    const newLimit = value === "all" ? "all" : Number(value);
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 0 // Reset to first page
    }));
  }, [setPagination]);

  // NEW: Handler for changing the page
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, [setPagination]);


  return (
    <>
      <div className="page-filter">
        <div className="common-pagination-settings">
          <label htmlFor="casesPerPageSelect">Cases per page:</label>
          <select
            id="casesPerPageSelect"
            value={pagination.limit} // Use limit from context
            onChange={handleCasesPerPageChange}
          >
            {[5, 10, 15, 20, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
            <option value="all">All</option>
          </select>
        </div>

        <div className="cases-options">

 {/* superAdmin */}
          {(role === "admin" || role === "superAdmin") && (
                <button
                  className="manage-cases-btn"
                  onClick={handleManageCases}
                >
                  Manage Cases
                </button>
              )}
          {role !== "customer" && (
            <>
              <button
                className="assign-cases-btn"
                onClick={() => {
                  if (showCheckboxes) {
                    setShowCheckboxes(false);
                    setShowLawyerDropdown(false);
                    setSelected({});
                    setSelectedLawyers([]);
                    setLawyerSearchTerm("");
                  } else {
                    setShowCheckboxes(true);
                  }
                }}
              >
                {showCheckboxes ? "Cancel" : "Assign Cases"}
              </button>
              
              
            </>
          )}

          {showCheckboxes && role !== "customer" && (
            <div className="lawyer-select-container">
              <button
                ref={lawyerButtonRef}
                className="select-lawyer-btn"
                onClick={() => setShowLawyerDropdown(!showLawyerDropdown)}
              >
                Select Lawyer{" "}
                {selectedLawyers.length > 0 && `(${selectedLawyers.length})`}
              </button>

              {showLawyerDropdown && 
                lawyerDropdownPosition.top !== 0 &&
                lawyerDropdownPosition.left !== 0 && (
                <div 
                  ref={lawyerDropdownRef}
                  className="lawyer-dropdown"
                  style={{
                    top: `${lawyerDropdownPosition.top}px`,
                    left: `${lawyerDropdownPosition.left}px`,
                    maxHeight: lawyerDropdownPosition.maxHeight
                      ? `${lawyerDropdownPosition.maxHeight}px`
                      : "500px",
                    overflowY: "auto",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search lawyers..."
                    value={lawyerSearchTerm}
                    onChange={(e) => setLawyerSearchTerm(e.target.value)}
                    className="lawyer-search-input"
                  />

                  <div className="lawyer-options">
                    {filteredLawyers.map((lawyer, index) => (
                      <div 
                        key={lawyer._id} 
                        className={`lawyer-option ${index === focusedLawyerIndex ? 'focused' : ''}`}
                        onClick={() => handleLawyerSelect(lawyer)}
                        style={{ cursor: 'pointer' }}
                        ref={(el) => {
                          if (el && index === focusedLawyerIndex) {
                            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLawyers.some(
                            (l) => l._id === lawyer._id
                          )}
                          onChange={() => handleLawyerSelect(lawyer)}
                        />
                        <span className="lawyer-name">{lawyer.name}</span>
                      </div>
                    ))}
                  </div>

                  {selectedLawyers.length > 0 && (
                    <div className="selected-lawyers">
                      <h4>Selected Lawyers:</h4>
                      <div className="selected-lawyers-list">
                        {selectedLawyers.map((lawyer) => (
                          <span
                            key={lawyer._id}
                            className="selected-lawyer-tag"
                          >
                            {lawyer.name}
                            <button onClick={() => handleLawyerSelect(lawyer)}>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="assign-action">
                    <button
                      className="assign-cases-final-btn"
                      onClick={handleAssignCases}
                      disabled={isAssigning}
                    >
                      {isAssigning ? "Assigning..." : "Assign Cases"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="filter-tab">
            <div className="filter-dropdown-wrapper">
              <button
                onClick={() => setIsFilterOpen((prev) => !prev)}
                className="filter-toggle-btn"
                ref={filterButtonRef}
              >
                {isFilterOpen ? "Hide Filters" : "Show Filters"}
              </button>

              {isFilterOpen &&
                dropdownPosition.top !== 0 &&
                dropdownPosition.left !== 0 && (
                  <div
                    className={`filter-dropdown-box main-case-filter-dropdown-box ${role === "customer" ? "customer-filters" : ""
                      }`}
                    ref={filterDropdownRef}
                    style={{
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      maxHeight: dropdownPosition.maxHeight
                        ? `${dropdownPosition.maxHeight}px`
                        : "500px",
                      overflowY: "auto",
                      position: "fixed",
                    }}
                  >
                    <div className="page-filter">
                      <div className="filter-group main-case-filter-filter-group">
                        <label>Status</label>
                        <select
                          value={filters.status}
                          onChange={(e) =>
                            handleFilterChange("status", e.target.value)
                          }
                        >
                          <option value="">All</option>
                          <option value="pending">Pending</option>
                          <option value="disposed">Disposed</option>
                          <option value="appealable">Appealable</option>
                          <option value="re-appealed">Re-Appealed</option>
                          {/* Add other status options as needed not for role customer */}
                          {role !== "customer" && (
                            <>
                              <option value="undated">Undated</option>
                            </>
                          )}
                        </select>
                      </div>


                      {role !== "customer" && (
                        <div className="filter-group main-case-filter-filter-group">
                          <label htmlFor="typeFilter">Type</label>
                          <select
                            id="typeFilter"
                            value={filters.type}
                            onChange={(e) =>
                              handleFilterChange("type", e.target.value)
                            }
                          >
                            <option value="all">All</option>
                            <option value="assigned">Assigned</option>
                            <option value="unassigned">Unassigned</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {role !== "customer" && (
                      <div className="filter-group">
                        <label>Filter by Label</label>
                        <div
                          className="label-filter-dropdown-wrapper"
                          style={{ position: "relative" }}
                        >
                          <div
                            className="label-filter-select"
                            onClick={() =>
                              setIsLabelDropdownOpen(!isLabelDropdownOpen)
                            }
                            style={{
                              border: "1px solid #ccc",
                              padding: "8px",
                              cursor: "pointer",
                              backgroundColor: "white",
                              borderRadius: "4px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontSize: "14px" }}>
                              {selectedLabelName || "Select Label"}
                            </span>
                          </div>

                          {isLabelDropdownOpen && (
                            <div
                              className="label-filter-dropdown"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderTop: "none",
                                maxHeight: "200px",
                                overflowY: "auto",
                                zIndex: 1000,
                                borderRadius: "0 0 4px 4px",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <input
                                  type="text"
                                  placeholder="Search labels..."
                                  value={labelSearchTerm}
                                  onChange={(e) =>
                                    setLabelSearchTerm(e.target.value)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "6px",
                                    border: "1px solid #ddd",
                                    borderRadius: "3px",
                                    fontSize: "14px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>

                              <div
                                onClick={() => handleLabelSelect("")}
                                style={{
                                  padding: "8px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                  backgroundColor:
                                    contextSelectedLabel === ""
                                      ? "#f0f0f0"
                                      : "transparent",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.backgroundColor = "#f5f5f5")
                                }
                                onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  contextSelectedLabel === ""
                                    ? "#f0f0f0"
                                    : "transparent")
                                }
                              >
                                All Labels
                              </div>

                              {filteredLabels.map((labelGroup) => {
                                const labelId = labelGroup.labels?.[0]?._id;
                                return (
                                  <div
                                    key={labelId}
                                    onClick={() => handleLabelSelect(labelId)}
                                    style={{
                                      padding: "8px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #eee",
                                      backgroundColor:
                                        contextSelectedLabel === labelId
                                          ? "#f0f0f0"
                                          : "transparent",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor = "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                    (e.target.style.backgroundColor =
                                      contextSelectedLabel === labelId
                                        ? "#f0f0f0"
                                        : "transparent")
                                    }
                                  >
                                    <div style={{ fontWeight: "500" }}>
                                      {labelGroup.name}
                                    </div>
                                  </div>
                                );
                              })}

                              {filteredLabels.length === 0 && labelSearchTerm && (
                                <div
                                  style={{
                                    padding: "8px",
                                    color: "#999",
                                    fontStyle: "italic",
                                  }}
                                >
                                  No labels found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {role !== "customer" && (
                      <div className="filter-group">
                        <label>Assigned To</label>
                        <div
                          className="user-filter-dropdown-wrapper"
                          style={{ position: "relative" }}
                        >
                          <div
                            className="user-filter-select"
                            onClick={() =>
                              setIsUserDropdownOpen(!isUserDropdownOpen)
                            }
                            style={{
                              border: "1px solid #ccc",
                              padding: "8px",
                              cursor: "pointer",
                              backgroundColor: "white",
                              borderRadius: "4px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontSize: "14px" }}>
                              {selectedUserName || "Select User"}
                            </span>
                          </div>

                          {isUserDropdownOpen && (
                            <div
                              className="user-filter-dropdown"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderTop: "none",
                                maxHeight: "200px",
                                overflowY: "auto",
                                zIndex: 1000,
                                borderRadius: "0 0 4px 4px",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <input
                                  type="text"
                                  placeholder="Search users..."
                                  value={userSearchTerm}
                                  onChange={(e) =>
                                    setUserSearchTerm(e.target.value)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "6px",
                                    border: "1px solid #ddd",
                                    borderRadius: "3px",
                                    fontSize: "14px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>

                              <div
                                onClick={() => handleUserSelect("")}
                                style={{
                                  padding: "8px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                  backgroundColor:
                                    selectedAssignedUser === ""
                                      ? "#f0f0f0"
                                      : "transparent",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.backgroundColor = "#f5f5f5")
                                }
                                onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  selectedAssignedUser === ""
                                    ? "#f0f0f0"
                                    : "transparent")
                                }
                              >
                                All Users
                              </div>

                              {filteredUsers.map((user) => (
                                <div
                                  key={user._id}
                                  onClick={() => handleUserSelect(user._id)}
                                  style={{
                                    padding: "8px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #eee",
                                    backgroundColor:
                                      selectedAssignedUser === user._id
                                        ? "#f0f0f0"
                                        : "transparent",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.target.style.backgroundColor = "#f5f5f5")
                                  }
                                  onMouseLeave={(e) =>
                                  (e.target.style.backgroundColor =
                                    selectedAssignedUser === user._id
                                      ? "#f0f0f0"
                                      : "transparent")
                                  }
                                >
                                  <div style={{ fontWeight: "500" }}>
                                    {user.name}
                                  </div>
                                </div>
                              ))}

                              {filteredUsers.length === 0 && userSearchTerm && (
                                <div
                                  style={{
                                    padding: "8px",
                                    color: "#999",
                                    fontStyle: "italic",
                                  }}
                                >
                                  No users found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Date filters remain the same */}
                    <div className="filter-group">
                      <label>Filing Date</label>
                      <div className="filter-one-row">
                        <input
                          type="date"
                          value={filters.filingDateFrom}
                          onChange={(e) =>
                            handleFilterChange("filingDateFrom", e.target.value)
                          }
                        />
                        <input
                          type="date"
                          value={filters.filingDateTo}
                          onChange={(e) =>
                            handleFilterChange("filingDateTo", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="filter-group">
                      <label>Registration Date</label>
                      <div className="filter-one-row">
                        <input
                          type="date"
                          value={filters.registrationDateFrom}
                          onChange={(e) =>
                            handleFilterChange(
                              "registrationDateFrom",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="date"
                          value={filters.registrationDateTo}
                          onChange={(e) =>
                            handleFilterChange(
                              "registrationDateTo",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div className="filter-group">
                      <label>First Hearing Date</label>
                      <div className="filter-one-row">
                        <input
                          type="date"
                          value={filters.firstHearingDateFrom}
                          onChange={(e) =>
                            handleFilterChange(
                              "firstHearingDateFrom",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="date"
                          value={filters.firstHearingDateTo}
                          onChange={(e) =>
                            handleFilterChange(
                              "firstHearingDateTo",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div className="filter-group">
                      <label>Decision Date</label>
                      <div className="filter-one-row">
                        <input
                          type="date"
                          value={filters.decisionDateFrom}
                          onChange={(e) =>
                            handleFilterChange(
                              "decisionDateFrom",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="date"
                          value={filters.decisionDateTo}
                          onChange={(e) =>
                            handleFilterChange("decisionDateTo", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="filter-actions">
                      <button className="clear-btn" onClick={clearFilters}>
                        Clear
                      </button>
                      <button
                        className="hide-btn"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Hide
                      </button>
                    </div>
                  </div>
                )}
            </div>
            {/* )} */}
          </div>
        </div>
      </div>


      {hasActiveFilters && (
        <div className="applied-filters">
          <span className="filters-text">Applied Filters:</span>

          {/* Search term filter pill */}
          {searchTerm && searchTerm.trim() && (
            <span
              className="filter-tag"
              onClick={() => {
                // Clear search term through context
                clearAllFilters();
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Search: {formatText(searchTerm)} ×
            </span>
          )}

          {/* Card selection filter pill */}
          {selectedCaseCard && (
            <span
              className="filter-tag"
              onClick={() => handleCaseCardClick(selectedCaseCard)}
            >
              Card: {formatText(selectedCaseCard)} ×
            </span>
          )}

          {/* Status filter pill */}
          {filters.status && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("status", "")}
            >
              Status: {formatText(filters.status)} ×
            </span>
          )}

          {/* Type filter pill */}
          {filters.type && filters.type !== "all" && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("type", "all")}
            >
              Type: {formatText(filters.type)} ×
            </span>
          )}

          {/* Assigned To filter pill */}
          {selectedAssignedUser && (
            <span
              className="filter-tag"
              onClick={() => handleUserSelect("")}
            >
              Assigned To: {users.find((u) => u._id === selectedAssignedUser)?.name || "User"} ×
            </span>
          )}

          {/* Label filter pill */}
          {contextSelectedLabel && (
            <span
              className="filter-tag"
              onClick={() => handleLabelSelect("")}
            >
              Label: {selectedLabelName || "Label"} ×
            </span>
          )}

          {/* Filing Date range pills */}
          {filters.filingDateFrom && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("filingDateFrom", "")}
            >
              Filing From: {filters.filingDateFrom} ×
            </span>
          )}

          {filters.filingDateTo && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("filingDateTo", "")}
            >
              Filing To: {filters.filingDateTo} ×
            </span>
          )}

          {/* Registration Date range pills */}
          {filters.registrationDateFrom && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("registrationDateFrom", "")}
            >
              Registration From: {filters.registrationDateFrom} ×
            </span>
          )}

          {filters.registrationDateTo && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("registrationDateTo", "")}
            >
              Registration To: {filters.registrationDateTo} ×
            </span>
          )}

          {/* First Hearing Date range pills */}
          {filters.firstHearingDateFrom && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("firstHearingDateFrom", "")}
            >
              First Hearing From: {filters.firstHearingDateFrom} ×
            </span>
          )}

          {filters.firstHearingDateTo && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("firstHearingDateTo", "")}
            >
              First Hearing To: {filters.firstHearingDateTo} ×
            </span>
          )}

          {/* Decision Date range pills */}
          {filters.decisionDateFrom && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("decisionDateFrom", "")}
            >
              Decision From: {filters.decisionDateFrom} ×
            </span>
          )}

          {filters.decisionDateTo && (
            <span
              className="filter-tag"
              onClick={() => handleFilterChange("decisionDateTo", "")}
            >
              Decision To: {filters.decisionDateTo} ×
            </span>
          )}
          

          {/* Clear All button */}
          <span
            className="filter-tag clear-all-tag"
            onClick={() => {
              clearAllFilters();
              // Clear DetailSection's search input  
              if (window.clearDetailSectionSearch) {
                window.clearDetailSectionSearch();
              }
              // Reset local UI state
              // setCurrentPage(0); // No longer needed
              setSelected({});
            }}
          >
            Clear All
          </span>
        </div>
      )}

      <div
        className={`case-list-wrapper ${isFilterOpen ? "min-height-applied" : ""
          }`}
      >
        {isLoading ? (
          <JusticeLoader message="Loading cases..." type="gavel" />
        ) : shouldShowNoCasesMessage ? (
          <div className="no-records-found">{getNoCasesMessage}</div>
        ) : (
          <>
            {showCheckboxes && paginatedCases.length > 0 && (
              <div className="select-all-container">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  id="selectAll"
                />
                <label htmlFor="selectAll">
                  Select All ({paginatedCases.length} cases on this page)
                </label>
              </div>
            )}


            {paginatedCases.map((c, i) => (
              <CaseCard
                key={`case-${selectedCourtType}-${selectedCaseType || "direct"
                  }-${pagination.page}-${i}-${c.viewDetails?.courtRef || c.id || i}`}
                selectedCourtType={selectedCourtType}
                selectedCaseType={selectedCaseType}
                caseData={c}
                isSelected={selected[i]}
                onSelectToggle={() => toggleOne(i)}
                onCaseDeleted={handleCaseDeleted}
                showCheckboxes={showCheckboxes}
                allReAppealData={c.reAppeal}
                serialNumber={pagination.limit === "all" ? i + 1 : pagination.page * pagination.limit + i + 1}
                searchTerm={searchTerm}
              />
            ))}
          </>
        )}
      </div>

      {/* MODIFIED: Backend Pagination Controls - Only show if limit is not "all" */}
      {pagination.limit !== "all" && pagination.totalCases > pagination.limit && (
        <div className="pagination-controls">
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(0)}
          >
            First
          </button>
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page + 1} of{" "}
            {Math.ceil(pagination.totalCases / pagination.limit)}
          </span>
          <button
            disabled={
              pagination.page >= Math.ceil(pagination.totalCases / pagination.limit) - 1
            }
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
          <button
            disabled={
              pagination.page >= Math.ceil(pagination.totalCases / pagination.limit) - 1
            }
            onClick={() =>
              handlePageChange(
                Math.ceil(pagination.totalCases / pagination.limit) - 1
              )
            }
          >
            Last
          </button>
        </div>
      )}

      {/* Company Limit Modal */}
      <CompanyLimitModal
        isOpen={isCompanyModalOpen}
        onClose={() => {
          setIsCompanyModalOpen(false);
          setPersistedSelectedRefs(new Set());
        }}
        selectedCompany={company}
        companyId={company?._id}
        selectedRefs={persistedSelectedRefs}
        setSelectedRefs={setPersistedSelectedRefs}
        onUpdateSuccess={handleModalUpdateSuccess}
        prefetchedStats={prefetchedStats}
      />
    </>
  );
}